import { DataTable, StatusBadge } from '@skylabs-digital/backoffice-kit';
import type { ColumnDef } from '@skylabs-digital/tables-react';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AppEntity } from '../../api/apps.api';
import { isManagedByCac, ManagedByCacBadge } from '../shared/ManagedByCac';

export function formatBytes(n: number | string | null | undefined): string {
  if (n === null || n === undefined) return '—';
  const v = typeof n === 'string' ? Number(n) : n;
  if (!Number.isFinite(v)) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let x = v;
  while (x >= 1024 && i < units.length - 1) {
    x /= 1024;
    i++;
  }
  return `${x.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

interface AppListProps {
  apps: AppEntity[];
  loading: boolean;
  error: Error | null | undefined;
}

export function AppList({ apps, loading, error }: AppListProps) {
  const navigate = useNavigate();
  const columns = useMemo<ColumnDef<AppEntity>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <span className="flex items-center gap-2 font-medium text-foreground">
            {row.original.name}
            {isManagedByCac(row.original) ? <ManagedByCacBadge /> : null}
          </span>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <StatusBadge tone={row.original.status === 'ORPHAN' ? 'danger' : 'success'}>
            {row.original.status === 'ORPHAN' ? 'Orphan' : 'Active'}
          </StatusBadge>
        ),
      },
      {
        accessorKey: 'storageQuotaBytes',
        header: 'Quota',
        cell: ({ row }) => (
          <span className="text-sm">{formatBytes(row.original.storageQuotaBytes)}</span>
        ),
      },
      {
        id: 'rules',
        header: 'Prefix rules',
        cell: ({ row }) => <span className="text-sm">{row.original.prefixRules.length}</span>,
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        cell: ({ row }) => (
          <span className="text-sm">{new Date(row.original.createdAt).toLocaleDateString()}</span>
        ),
      },
    ],
    [],
  );

  return (
    <DataTable
      columns={columns}
      data={apps}
      isLoading={loading}
      error={error}
      onRowClick={(app) => navigate(`/apps/${app.id}`)}
    />
  );
}
